import { autoinject, computedFrom } from "aurelia-framework";
import { DialogController } from "aurelia-dialog";
import {
  ControllerValidateResult,
  ValidationController,
  ValidationControllerFactory,
  ValidationRules,
} from "aurelia-validation";
import { HttpResponseMessage } from "aurelia-http-client";
import { I18N } from "aurelia-i18n";
import { BootstrapFormRenderer } from "../../validation/bootstrap-form-renderer";
import { HttpClientService } from "../../services/http-client-service";
import { User } from "./user";

@autoinject
export class EditUser {
  user: User;
  mode: "edit" | "new";
  roles: string[];

  private validationController: ValidationController;

  constructor(
    private dialogController: DialogController,
    private validationControllerFactory: ValidationControllerFactory,
    private httpClientService: HttpClientService,
    private i18n: I18N,
  ) {
    this.validationController =
      this.validationControllerFactory.createForCurrentScope();
    this.validationController.addRenderer(new BootstrapFormRenderer());

    this.httpClientService
      .get()
      .get("admin/user/roles")
      .then((r: HttpResponseMessage) => {
        this.roles = r.content;
      });
  }

  activate(model: { user: User; mode: "edit" | "new" }) {
    this.user = model.user;
    ValidationRules.ensure((d: User) => d.logon_id)
      .displayName(this.displayName("logon_id"))
      .required()
      .maxLength(200)
      .then()
      .satisfies(
        (logon_id: string) =>
          new Promise<boolean>((resolve, reject) =>
            this.httpClientService
              .get()
              .get("admin/user/exists?username=" + encodeURIComponent(logon_id))
              .then((r) => resolve(!r.content))
              .catch((e) => reject(e)),
          ),
      )
      .when(() => this.mode == "new")
      .withMessageKey("auth.usernameAlreadyExists")

      .ensure((d: User) => d.firstname)
      .displayName(this.displayName("firstname"))
      .maxLength(200)

      .ensure((d: User) => d.lastname)
      .displayName(this.displayName("lastname"))
      .maxLength(200)

      .ensure((d: User) => d.email)
      .displayName(this.displayName("email"))
      .email()
      .maxLength(200)

      .ensure((d: User) => d.role)
      .displayName(this.displayName("role"))
      .maxLength(200)

      .ensure((d: User) => d.newPassword)
      .displayName(this.displayName("newPassword"))
      .required()
      .when(() => this.mode == "new")
      .minLength(4)
      .maxLength(200)

      .on(this.user);

    this.mode = model.mode;
    return true;
  }

  @computedFrom("user.logon_id")
  get isAdmin(): boolean {
    return this.user.logon_id == "admin";
  }

  @computedFrom("mode")
  get isNewMode(): boolean {
    return this.mode == "new";
  }

  ok() {
    if (this.validationController.validating) return;
    this.validationController
      .validate()
      .then((result: ControllerValidateResult) => {
        if (result.valid) this.dialogController.ok(this.user);
      });
  }

  cancel() {
    this.dialogController.cancel();
  }

  private displayName(s: string) {
    return this.i18n.tr("admin.users.table.column." + s);
  }
}
